// =============================================================================
// SimSense Platform — Confirm Dialog
// =============================================================================

import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from "react";

interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [dialog, setDialog] = useState<ConfirmOptions | null>(null);
  const resolveRef = useRef<((v: boolean) => void) | null>(null);

  const confirm = useCallback((opts: ConfirmOptions): Promise<boolean> => {
    return new Promise(resolve => {
      resolveRef.current = resolve;
      setDialog(opts);
    });
  }, []);

  const handleClose = useCallback((result: boolean) => {
    resolveRef.current?.(result);
    resolveRef.current = null;
    setDialog(null);
  }, []);

  // Escape key closes dialog
  useEffect(() => {
    if (!dialog) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dialog, handleClose]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {dialog && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
            animation: "confirm-fade-in 150ms ease-out",
          }}
          onClick={() => handleClose(false)}
        >
          <div
            style={{
              background: "var(--bg2, #111827)",
              border: "1px solid var(--border, rgba(148,163,184,0.12))",
              borderRadius: "var(--radius, 12px)",
              padding: "24px",
              maxWidth: "420px",
              width: "90%",
              boxShadow: "var(--shadow-lg, 0 8px 24px rgba(0,0,0,0.4))",
              animation: "confirm-scale-in 150ms ease-out",
            }}
            onClick={e => e.stopPropagation()}
          >
            {dialog.title && (
              <h3 style={{
                margin: "0 0 8px 0",
                fontSize: "16px",
                fontWeight: 700,
                color: "var(--text1, #e2e8f0)",
              }}>
                {dialog.title}
              </h3>
            )}
            <p style={{
              margin: "0 0 20px 0",
              fontSize: "14px",
              color: "var(--text2, #94a3b8)",
              lineHeight: 1.5,
            }}>
              {dialog.message}
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button
                onClick={() => handleClose(false)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "var(--radius-sm, 8px)",
                  border: "1px solid var(--border, rgba(148,163,184,0.12))",
                  background: "transparent",
                  color: "var(--text2, #94a3b8)",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {dialog.cancelLabel || "Cancel"}
              </button>
              <button
                onClick={() => handleClose(true)}
                style={{
                  padding: "8px 16px",
                  borderRadius: "var(--radius-sm, 8px)",
                  border: "none",
                  background: dialog.danger ? "var(--bad, #ef4444)" : "var(--accent, #3b82f6)",
                  color: "#fff",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {dialog.confirmLabel || "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes confirm-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes confirm-scale-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </ConfirmContext.Provider>
  );
}
